<template>
    <div class="captcha" ref="stage">

        <div class="captcha__head">
            <span class="captcha__hint">{{ $t('interface.captcha_hint') }}</span>
            <i class="pi pi-refresh captcha__refresh" :title="$t('interface.captcha_refresh')" @click="generate"></i>
        </div>

        <div class="captcha__stage" :style="{ height: height + 'px' }">
            <canvas ref="bg" class="captcha__canvas"></canvas>
            <canvas
                ref="piece"
                class="captcha__piece"
                :style="{ transform: `translateX(${piece_x}px)` }"
            ></canvas>

            <transition name="captcha-flash">
                <div v-if="state === 'solved'" class="captcha__badge captcha__badge_ok">
                    <i class="pi pi-check"></i> {{ $t('interface.captcha_success') }}
                </div>
            </transition>

            <transition name="captcha-flash">
                <div v-if="state === 'failed'" class="captcha__badge captcha__badge_fail">
                    <i class="pi pi-times"></i> {{ $t('interface.captcha_failed') }}
                </div>
            </transition>
        </div>

        <div class="captcha__track" :class="`captcha__track_${state}`" :style="{ width: canvas_width + 'px' }">
            <div class="captcha__fill" :style="{ width: handle_x + handle_size / 2 + 'px' }"></div>

            <div
                class="captcha__handle"
                role="slider"
                :aria-label="$t('interface.captcha_slider_label')"
                :aria-valuemin="0"
                :aria-valuemax="max_handle"
                :aria-valuenow="Math.round(handle_x)"
                :tabindex="state === 'solved' ? -1 : 0"
                :style="{ transform: `translateX(${handle_x}px)` }"
                @mousedown="startDrag"
                @touchstart="startDrag"
                @keydown="onKeydown"
            >
                <i :class="handleIcon"></i>
            </div>
        </div>

    </div>
</template>

<script>
export default {
    name: 'SliderPuzzleCaptcha',

    props: {
        width: { type: Number, default: 300 },
        height: { type: Number, default: 150 },
        pieceSize: { type: Number, default: 46 },
        tolerance: { type: Number, default: 6 }
    },

    computed: {
        max_handle() {
            return Math.max(1, this.canvas_width - this.handle_size)
        },

        max_piece() {
            return Math.max(1, this.canvas_width - this.pieceSize)
        },

        handleIcon() {
            if(this.state === 'solved') return 'pi pi-check'
            if(this.state === 'failed') return 'pi pi-times'

            return 'pi pi-arrow-right'
        }
    },

    mounted() {
        this.$nextTick(this.resizeAndGenerate)
        window.addEventListener('resize', this.resizeAndGenerate)
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.resizeAndGenerate)
        this.stopListeners()
    },

    methods: {
        resizeAndGenerate() {
            const available = this.$refs.stage ? this.$refs.stage.clientWidth : 0

            this.canvas_width = available ? Math.max(240, Math.min(this.width, available)) : this.width

            this.generate()
        },

        /*
            Draws a procedural background, cuts a jigsaw-shaped hole at a random
            position and renders the matching piece on the overlay canvas.
            No image assets involved.
        */
        generate() {
            const bg = this.$refs.bg
            const piece = this.$refs.piece

            if(!bg || !piece) return

            const w = this.canvas_width
            const h = this.height
            const size = this.pieceSize
            const knob = Math.round(size * 0.2)

            this.state = 'idle'
            this.handle_x = 0
            this.piece_x = 0

            this.target_x = this.randomBetween(Math.round(size * 1.4), w - size - 10)
            this.piece_y = this.randomBetween(knob + 6, h - size - 8)

            const source = this.paintBackground(w, h)

            bg.width = w
            bg.height = h
            const bgCtx = bg.getContext('2d')
            bgCtx.clearRect(0, 0, w, h)
            bgCtx.drawImage(source, 0, 0)

            // the hole
            this.tracePiece(bgCtx, this.target_x, this.piece_y, size, knob)
            bgCtx.fillStyle = 'rgba(0, 8, 14, .62)'
            bgCtx.fill()
            bgCtx.strokeStyle = 'rgba(255, 255, 255, .45)'
            bgCtx.lineWidth = 1.5
            bgCtx.stroke()

            // the movable piece, cut from the untouched background
            piece.width = size + knob * 2
            piece.height = h
            const pieceCtx = piece.getContext('2d')
            pieceCtx.clearRect(0, 0, piece.width, piece.height)
            pieceCtx.save()
            this.tracePiece(pieceCtx, 0, this.piece_y, size, knob)
            pieceCtx.clip()
            pieceCtx.drawImage(source, -this.target_x, 0)
            pieceCtx.restore()

            this.tracePiece(pieceCtx, 0, this.piece_y, size, knob)
            pieceCtx.strokeStyle = 'rgba(255, 255, 255, .85)'
            pieceCtx.lineWidth = 1.5
            pieceCtx.stroke()
        },

        paintBackground(w, h) {
            const source = document.createElement('canvas')
            source.width = w
            source.height = h

            const ctx = source.getContext('2d')

            const gradient = ctx.createLinearGradient(0, 0, w, h)
            gradient.addColorStop(0, '#001521')
            gradient.addColorStop(0.55, '#0a3a55')
            gradient.addColorStop(1, '#00a4ff')
            ctx.fillStyle = gradient
            ctx.fillRect(0, 0, w, h)

            for(let i = 0; i < 7; i++) {
                ctx.beginPath()
                ctx.arc(this.randomBetween(0, w), this.randomBetween(0, h), this.randomBetween(12, 46), 0, Math.PI * 2)
                ctx.fillStyle = `rgba(255, 255, 255, ${(this.randomBetween(3, 11) / 100).toFixed(2)})`
                ctx.fill()
            }

            ctx.lineWidth = 2
            for(let i = 0; i < 3; i++) {
                ctx.beginPath()
                ctx.moveTo(this.randomBetween(-20, w), -10)
                ctx.lineTo(this.randomBetween(0, w + 20), h + 10)
                ctx.strokeStyle = 'rgba(255, 255, 255, .08)'
                ctx.stroke()
            }

            return source
        },

        // Square with a knob on the top edge and a matching notch on the left edge.
        tracePiece(ctx, x, y, size, knob) {
            const cx = x + size / 2
            const cy = y + size / 2

            ctx.beginPath()
            ctx.moveTo(x, y)
            ctx.lineTo(cx - knob, y)
            ctx.arc(cx, y, knob, Math.PI, 0, false)
            ctx.lineTo(x + size, y)
            ctx.lineTo(x + size, y + size)
            ctx.lineTo(x, y + size)
            ctx.lineTo(x, cy + knob)
            ctx.arc(x, cy, knob, Math.PI / 2, -Math.PI / 2, true)
            ctx.lineTo(x, y)
            ctx.closePath()
        },

        randomBetween(min, max) {
            return Math.floor(Math.random() * (max - min + 1)) + min
        },

        startDrag(event) {
            if(this.state === 'solved' || this.dragging) return

            this.dragging = true
            this.drag_origin = this.pointerX(event) - this.handle_x

            document.addEventListener('mousemove', this.onDrag)
            document.addEventListener('mouseup', this.endDrag)
            document.addEventListener('touchmove', this.onDrag, { passive: false })
            document.addEventListener('touchend', this.endDrag)
            document.addEventListener('touchcancel', this.endDrag)
        },

        onDrag(event) {
            if(!this.dragging) return

            // keep the page from scrolling while the handle is being dragged
            if(event.cancelable) event.preventDefault()

            this.moveHandle(this.pointerX(event) - this.drag_origin)
        },

        endDrag() {
            if(!this.dragging) return

            this.dragging = false
            this.stopListeners()
            this.verify()
        },

        stopListeners() {
            document.removeEventListener('mousemove', this.onDrag)
            document.removeEventListener('mouseup', this.endDrag)
            document.removeEventListener('touchmove', this.onDrag)
            document.removeEventListener('touchend', this.endDrag)
            document.removeEventListener('touchcancel', this.endDrag)
        },

        pointerX(event) {
            const touch = event.touches && event.touches[0]

            return touch ? touch.clientX : event.clientX
        },

        moveHandle(value) {
            this.handle_x = Math.max(0, Math.min(this.max_handle, value))
            this.piece_x = this.handle_x / this.max_handle * this.max_piece
        },

        onKeydown(event) {
            if(this.state === 'solved') return

            const step = event.shiftKey ? 12 : 4

            if(event.key === 'ArrowRight') {
                event.preventDefault()
                return this.moveHandle(this.handle_x + step)
            }

            if(event.key === 'ArrowLeft') {
                event.preventDefault()
                return this.moveHandle(this.handle_x - step)
            }

            if(event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                event.preventDefault()
                this.verify()
            }
        },

        verify() {
            // a plain click on the handle is not an attempt — do not punish it
            if(this.state === 'solved' || this.handle_x <= 0) return

            if(Math.abs(this.piece_x - this.target_x) <= this.tolerance) {
                this.state = 'solved'
                this.piece_x = this.target_x
                this.$emit('solved')
                return
            }

            this.state = 'failed'
            this.$emit('failed')

            setTimeout(() => {
                this.handle_x = 0
                this.piece_x = 0
            }, 240)

            setTimeout(() => {
                if(this.state === 'failed') this.generate()
            }, 900)
        }
    },

    data() {
        return {
            canvas_width: this.width,
            handle_size: 40,
            handle_x: 0,
            piece_x: 0,
            target_x: 0,
            piece_y: 0,
            dragging: false,
            drag_origin: 0,
            state: 'idle' // idle | failed | solved
        }
    }
}
</script>

<style lang="sass" scoped>
.captcha
    background: white
    border-radius: 5px
    box-shadow: $shadow
    padding: $r * 1.5
    max-width: 100%
    user-select: none

.captcha__head
    display: flex
    align-items: center
    justify-content: space-between
    margin-bottom: $r

.captcha__hint
    font-size: .85em
    color: $font-gray
    line-height: 1.3
    padding-right: $r

.captcha__refresh
    color: $main-blue
    cursor: pointer
    font-size: 1em
    transition: transform .25s ease-in-out

    &:hover
        transform: rotate(-90deg)

.captcha__stage
    position: relative
    border-radius: 5px
    overflow: hidden

.captcha__canvas
    display: block
    border-radius: 5px

.captcha__piece
    position: absolute
    top: 0
    left: 0
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, .45))
    will-change: transform

.captcha__badge
    position: absolute
    left: 50%
    bottom: $r
    transform: translateX(-50%)
    display: flex
    align-items: center
    font-size: .8em
    font-weight: bold
    color: white
    padding: 3px 10px
    border-radius: 3px
    white-space: nowrap

    i
        font-size: .8em
        margin-right: 4px

.captcha__badge_ok
    background: $main-green

.captcha__badge_fail
    background: $main-red

.captcha-flash-enter-active, .captcha-flash-leave-active
    transition: opacity .2s

.captcha-flash-enter, .captcha-flash-leave-to
    opacity: 0

.captcha__track
    position: relative
    height: 40px
    margin-top: $r * 1.5
    max-width: 100%
    background: #f2f5f7
    border: 1px solid #e3e9ed
    border-radius: 5px
    transition: border-color .2s, background .2s

.captcha__track_solved
    border-color: $main-green
    background: rgba(0, 165, 80, .08)

.captcha__track_failed
    border-color: $main-red
    background: rgba(255, 0, 0, .06)

.captcha__fill
    position: absolute
    top: 0
    left: 0
    height: 100%
    background: rgba(0, 164, 255, .15)
    border-radius: 5px 0 0 5px

.captcha__handle
    position: absolute
    top: -1px
    left: -1px
    width: 40px
    height: 40px
    display: flex
    align-items: center
    justify-content: center
    background: white
    border: 1px solid rgba(0, 164, 255, .4)
    border-radius: 5px
    box-shadow: $shadow
    color: $main-blue
    cursor: grab
    transition: box-shadow .2s, border-color .2s
    will-change: transform

    &:active
        cursor: grabbing

    &:hover, &:focus
        border-color: $main-blue
        box-shadow: 0 0 0 3px rgba(0, 164, 255, .18)
        outline: none

.captcha__track_solved .captcha__handle
    border-color: $main-green
    color: $main-green
    cursor: default

.captcha__track_failed .captcha__handle
    border-color: $main-red
    color: $main-red

@media only screen and (max-width: 540px)
    .captcha__hint
        font-size: .78em
</style>
